import { createEffect, createEvent, createStore, sample } from 'effector';
import { $locale } from '../../entities/preferences';
import { loadMethodsFx } from '../../entities/session';
import { authApi } from '../../shared/api/auth';
import { ApiRequestError } from '../../shared/api/http';
import { routes } from '../../shared/routing';

/*
 * Sign-in page: OAuth buttons (full-page redirects) and the email magic-link form.
 */

export const emailSubmitted = createEvent<string>();
export const differentEmailRequested = createEvent();

export const $returnTo = createStore('/').on(
  [routes.signIn.opened, routes.signIn.updated],
  (_, { query }) => (typeof query.returnTo === 'string' ? query.returnTo : '/'),
);

export const requestLinkFx = createEffect(
  ({ email, locale, returnTo }: { email: string; locale: string; returnTo: string }) =>
    authApi.requestMagicLink(email, locale, returnTo),
);

/** Where the link was sent (the "check your email" state). */
export const $sentTo = createStore<string | null>(null)
  .on(requestLinkFx.done, (_, { params }) => params.email)
  .reset(differentEmailRequested, routes.signIn.closed);

/** An error code: from the URL (OAuth failures) or from the last request. */
export const $error = createStore<string | null>(null)
  .on([routes.signIn.opened, routes.signIn.updated], (_, { query }) =>
    typeof query.error === 'string' ? query.error : null,
  )
  .on(requestLinkFx.failData, (_, error) =>
    error instanceof ApiRequestError ? error.code : 'generic',
  )
  .reset(requestLinkFx.done, differentEmailRequested);

sample({ clock: routes.signIn.opened, target: loadMethodsFx });
sample({
  clock: emailSubmitted,
  source: { locale: $locale, returnTo: $returnTo },
  fn: ({ locale, returnTo }, email) => ({ email, locale, returnTo }),
  target: requestLinkFx,
});
